import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { ApiError, type ApiClient } from '../api/client';
import type { AppItem, AppStage, StageListResponse } from '../types';
import { appendRequestId } from '../utils/errors';
import { chooseContainer, createPlan, guestRepository, initialDraft, planDraft, setupMessage,
  type PersonalAgentPlan, type RuntimeDraft, type RuntimeRelease, type SetupStatus } from './personalAgentSettings';

const endpoint = '/api/settings/openclaw-personal-plan';
const deploymentPath = (app: string, stage: string) => `/api/apps/${app}/stages/${stage}/observed-releases`;

export default function PersonalAgentSettingsPage({ apiClient }: { apiClient: ApiClient }) {
  const [status, setStatus] = useState<SetupStatus | null>(null);
  const [apps, setApps] = useState<AppItem[]>([]);
  const [stages, setStages] = useState<AppStage[]>([]);
  const [appId, setAppId] = useState('');
  const [stageId, setStageId] = useState('');
  const [containerId, setContainerId] = useState('');
  const [release, setRelease] = useState<RuntimeRelease | null>(null);
  const [draft, setDraft] = useState<RuntimeDraft>(initialDraft);
  const [busy, setBusy] = useState(false);
  const [uncertain, setUncertain] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const pending = useRef<PersonalAgentPlan | null>(null);

  const refresh = useCallback(async () => {
    const next = await apiClient.get<SetupStatus>(endpoint);
    setStatus(next); setUncertain(false);
    return next;
  }, [apiClient]);
  useEffect(() => {
    const controller = new AbortController();
    Promise.all([apiClient.get<SetupStatus>(endpoint, { signal: controller.signal }),
      apiClient.get<AppItem[]>('/api/apps', { signal: controller.signal })]).then(([next, available]) => {
      if (controller.signal.aborted) return;
      setStatus(next); setApps(available);
      setAppId(next.plan?.app_id ?? available.find(app => app.is_self)?.id ?? '');
      if (next.plan) setStageId(next.plan.release.stage_id);
    }).catch(() => { if (!controller.signal.aborted) setError('Assistant settings could not be loaded. Sign in as an administrator and check again.'); });
    return () => controller.abort();
  }, [apiClient]);
  useEffect(() => {
    setStages([]); setRelease(null);
    if (!appId || status?.plan) return;
    const controller = new AbortController();
    apiClient.get<StageListResponse>(`/api/apps/${appId}/stages`, { signal: controller.signal }).then(next => {
      if (controller.signal.aborted) return;
      setStages(next.stages);
      if (next.stages.length === 1) setStageId(next.stages[0].id);
    }).catch(() => { if (!controller.signal.aborted) setError('Deployment environments could not be loaded.'); });
    return () => controller.abort();
  }, [apiClient, appId, status?.plan]);
  useEffect(() => {
    setRelease(null);
    if (!appId || !stageId || status?.plan) return;
    const controller = new AbortController();
    apiClient.get<{ release: RuntimeRelease | null }>(deploymentPath(appId, stageId), { signal: controller.signal }).then(next => {
      if (controller.signal.aborted) return;
      setRelease(next.release);
      setContainerId(current => chooseContainer(next.release, current));
    }).catch(() => { if (!controller.signal.aborted) setError('The verified deployment could not be loaded.'); });
    return () => controller.abort();
  }, [apiClient, appId, stageId, status?.plan]);

  const save = async (event: FormEvent) => {
    event.preventDefault();
    if (!release || busy || uncertain) return;
    setBusy(true); setError(null); setNotice(null);
    try {
      const latest = await apiClient.get<{ release: RuntimeRelease | null }>(deploymentPath(appId, stageId));
      if (!latest.release || latest.release.id !== release.id) {
        setRelease(latest.release); setContainerId('');
        throw new Error('The deployment changed. Review the current deployment and select its backend container before approving setup.');
      }
      const attempt = createPlan(draft, latest.release, containerId, pending.current?.admission_id ?? crypto.randomUUID());
      pending.current = attempt;
      try { await apiClient.post(endpoint, attempt); }
      catch (failure) {
        setUncertain(!(failure instanceof ApiError && [400, 401, 403, 409].includes(failure.status)));
        throw failure;
      }
      setStatus({ plan: attempt, legacy_configured: false, ready: true, readiness_error: null });
      setNotice('Assistant setup is approved. Open Personal agent to start your own assistant.');
      try { await refresh(); }
      catch { setNotice('Setup was saved. Check saved setup to confirm its current readiness.'); }
    } catch (failure) {
      const message = failure instanceof ApiError
        ? failure.status === 409 ? 'An existing approval cannot be replaced. Check saved setup before making another change.'
          : failure.status === 503 ? 'The deployment, executor, registry access, and private model connection must be ready before approval. Check saved setup and review server readiness.'
            : failure.status === 401 || failure.status === 403 ? 'A signed-in administrator session is required to approve setup.'
              : 'Setup was not confirmed. Check saved setup before trying again.'
        : failure instanceof Error ? failure.message : 'Setup could not be approved.';
      setError(appendRequestId(message, failure instanceof ApiError ? failure.requestId : undefined));
    } finally { setBusy(false); }
  };
  const check = async () => {
    setBusy(true); setError(null);
    try { await refresh(); }
    catch { setError('Saved setup could not be checked.'); }
    finally { setBusy(false); }
  };
  const toggle = async () => {
    if (!status?.plan || busy || uncertain) return;
    setBusy(true); setError(null); setNotice(null);
    try {
      await apiClient.post(endpoint, { ...status.plan, enabled: !status.plan.enabled });
      await refresh();
    } catch (failure) {
      setUncertain(true);
      setError(appendRequestId('The change was not confirmed. Check saved setup before trying again.', failure instanceof ApiError ? failure.requestId : undefined));
    } finally { setBusy(false); }
  };
  const importRuntime = async (file: File | undefined) => {
    if (!file) return;
    try {
      if (file.size > 32768) throw new Error();
      const imported = JSON.parse(await file.text()) as PersonalAgentPlan;
      // Import only form defaults. The saved binding is rebuilt from the fresh server deployment.
      const values = planDraft(imported);
      if (!Object.entries(initialDraft).every(([key, sample]) => {
        const value = values[key as keyof RuntimeDraft];
        return typeof value === typeof sample && (typeof value !== 'number' || Number.isFinite(value));
      })) throw new Error();
      if (!/^[a-f0-9-]{36}$/.test(imported.app_id) || !/^[a-f0-9-]{36}$/.test(imported.release.stage_id)
        || !apps.some(app => app.id === imported.app_id)) throw new Error();
      setDraft(values); setAppId(imported.app_id); setStageId(imported.release.stage_id);
      setContainerId(release?.app_id === imported.app_id && release.stage_id === imported.release.stage_id
        ? chooseContainer(release, imported.release.container_id) : imported.release.container_id);
      pending.current = null;
      setError(null); setNotice('Runtime defaults imported. Review the current deployment and limits before approving setup.');
    } catch { setError('Choose a valid approved VM runtime plan for an application you can access.'); }
  };
  const numericFields: Array<[keyof Omit<RuntimeDraft, 'image' | 'configDigest'>, string, number, number | undefined, number]> = [
    ['memory', 'Memory (MiB)', 2048, 8192, 1], ['cpus', 'Virtual CPUs', 1, 4, 1], ['disk', 'Disk limit (GiB)', 8, 64, 1],
    ['minutes', 'Allowance duration (minutes)', 1, 60, 1 / 60], ['requests', 'Maximum model requests', 1, Number.MAX_SAFE_INTEGER, 1],
    ['tokens', 'Maximum model tokens', 1, Number.MAX_SAFE_INTEGER, 1], ['cost', 'Maximum model cost', 0.01, undefined, 0.01],
  ];
  const current = status?.plan;
  return <main className="page">
    <p><Link to="/settings">Settings</Link> / Personal agent</p>
    <section className="card"><h1>Personal agent setup</h1>
      <p>Approve the assistant’s VM runtime and model allowance once. Each owner then starts their own private assistant from the Personal agent page.</p>
      <p role="status">{!status ? 'Checking setup…' : status.ready ? 'Ready for owners to start their assistants.' : setupMessage(status.readiness_error)}</p>
      {error && <p role="alert">{error}</p>}{notice && <p role="status">{notice}</p>}
      <p><Link to="/personal-agent/legacy">Open Personal agent</Link></p>
      <button type="button" disabled={busy} onClick={() => { void check(); }}>Check saved setup</button>
    </section>
    {current && <section className="card"><h2>Approved runtime</h2>
      <p>{current.vm.resources.memory_mib} MiB memory, {current.vm.resources.vcpu_count} virtual CPUs, {current.vm.resources.disk_bytes / 1024 ** 3} GiB disk limit.</p>
      <p>Allowance: {current.ttl_seconds / 60} minutes, at most {current.max_requests} model requests, {current.max_tokens} tokens and {current.max_cost} cost units. Limits do not refill automatically.</p>
      <details><summary>Runtime details</summary><p>Guest image: {current.vm.guest.reference}</p><p>Executor: {current.release.executor_id}</p><p>Approved backend revision: {current.reviewed_source_sha}</p></details>
      <p>Saved runtime and allowance limits are fixed. Disabling new starts does not replace or renew an owner’s existing allowance.</p>
      <button type="button" disabled={busy || uncertain} onClick={() => { void toggle(); }}>{current.enabled ? 'Disable new starts' : 'Enable new starts'}</button>
    </section>}
    {status && !current && <section className="card"><h2>Approve a runtime</h2>
      {status.legacy_configured && <p>A legacy runtime is recorded. Its existing owner history and cleanup remain available; this form approves a separate VM runtime.</p>}
      <p>Saving enables owner Start. VM creation and model credentials require the owner’s separate Start action.</p>
      <label>Import approved runtime defaults (optional) <input type="file" accept="application/json,.json" disabled={busy || uncertain} onChange={event => { void importRuntime(event.target.files?.[0]); event.target.value = ''; }} /></label>
      <form onSubmit={event => { void save(event); }}>
        <fieldset disabled={busy || uncertain}><legend>Backend deployment</legend>
          <p>Choose the Mission Control backend deployment that will connect to the assistant.</p>
          <div className="form-row"><label htmlFor="assistant-app">Application</label><select id="assistant-app" required value={appId} onChange={event => { setAppId(event.target.value); setStageId(''); setContainerId(''); setError(null); }}>
            <option value="">Choose an application</option>{apps.map(app => <option key={app.id} value={app.id}>{app.name}</option>)}
          </select></div>
          <div className="form-row"><label htmlFor="assistant-stage">Environment</label><select id="assistant-stage" required value={stageId} onChange={event => { setStageId(event.target.value); setContainerId(''); setError(null); }}>
            <option value="">Choose an environment</option>{stages.map(stage => <option key={stage.id} value={stage.id}>{stage.name}</option>)}
          </select></div>
          {stageId && !release && <p>A verified current deployment is required for this environment.</p>}
          <div className="form-row"><label htmlFor="assistant-container">Backend container</label><select id="assistant-container" required value={containerId} onChange={event => setContainerId(event.target.value)}>
            <option value="">Choose the backend container</option>{release?.containers.map(container => <option key={container.container_id} value={container.container_id}>{container.container_name}</option>)}
          </select></div>
          {release && <p>Executor: {release.executor_id}. Backend revision: {release.commit_sha.slice(0, 12)}.</p>}
        </fieldset>
        <fieldset disabled={busy || uncertain}><legend>VM runtime</legend>
          <p>Use the immutable guest image and config digests from the approved guest publication receipt.</p>
          <div className="form-row"><label htmlFor="assistant-image">Guest image</label><input id="assistant-image" required value={draft.image} placeholder={`${guestRepository}@sha256:…`} onChange={event => { const image = event.target.value.trim(); setDraft(current => ({ ...current, image })); }} /></div>
          <div className="form-row"><label htmlFor="assistant-config">Guest config digest</label><input id="assistant-config" required value={draft.configDigest} placeholder="sha256:…" onChange={event => { const configDigest = event.target.value.trim(); setDraft(current => ({ ...current, configDigest })); }} /></div>
          {numericFields.map(([key, label, min, max, step]) => <div className="form-row" key={key}><label htmlFor={`assistant-${key}`}>{label}</label><input id={`assistant-${key}`} required type="number" min={min} max={max} step={step} value={draft[key]} onChange={event => { const value = event.target.valueAsNumber; setDraft(current => ({ ...current, [key]: value })); }} /></div>)}
          <p>The disk limit uses the executor’s existing VM store. This setup does not increase its allocated capacity. The finite model allowance starts when the owner starts an assistant.</p>
        </fieldset>
        <button type="submit" disabled={busy || uncertain || !release || !containerId}>Approve assistant setup</button>
      </form>
    </section>}
  </main>;
}
